import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { Copy, DollarSign, TrendingUp, Layers, FileText, Wallet, ArrowRight, Recycle, Sparkles } from "lucide-react";
import { endpoints } from "@/lib/api";
import { usePersona } from "@/lib/personaStore";
import { useBranding } from "@/lib/branding";
import { displayReqNumber } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const COLORS = ["#0033A0", "#DA291C", "#0084C7", "#F5B700", "#0F9B7F", "#7C3AED"];

function Kpi({ label, value, icon: Icon, accent }) {
  return (
    <Card className={`p-5 border-slate-200 shadow-sm ${accent ? "bg-[#0033A0] text-white border-transparent" : ""}`}>
      <div className={`flex items-center justify-between ${accent ? "text-white/70" : "text-slate-500"}`}>
        <span className="text-[11px] uppercase tracking-widest font-semibold">{label}</span>
        <Icon size={14} />
      </div>
      <div className={`font-display font-black text-3xl mt-1 ${accent ? "text-white" : "text-slate-900"}`}>{value}</div>
    </Card>
  );
}

function fmtUsd(v) {
  return `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export default function Finance() {
  const { persona } = usePersona();
  const { brand } = useBranding();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [personaOnly, setPersonaOnly] = useState(false);
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [aribaData, setAribaData] = useState(null);
  const [aribaLoading, setAribaLoading] = useState(false);
  const [story, setStory] = useState(null);

  useEffect(() => {
    endpoints.lifecycleStory().then(setStory).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      endpoints.finance(personaOnly ? persona.id : null),
      endpoints.orders(personaOnly ? persona.id : null),
    ]).then(([f, o]) => { setData(f); setOrders(o); setLoading(false); })
      .catch(() => setLoading(false));
  }, [personaOnly, persona.id]);

  const openAriba = async (orderId) => {
    setSelectedOrder(orderId);
    setAribaLoading(true);
    setAribaData(null);
    try {
      const d = await endpoints.aribaPayload(orderId);
      setAribaData(d);
    } catch { toast.error("Could not load Ariba payload"); }
    finally { setAribaLoading(false); }
  };

  const copy = (obj) => {
    navigator.clipboard.writeText(JSON.stringify(obj, null, 2)).then(
      () => toast.success("Copied to clipboard"),
      () => toast.error("Copy failed")
    );
  };

  if (loading || !data) {
    return <div className="grid grid-cols-4 gap-4"><Skeleton className="h-24" /><Skeleton className="h-24" /><Skeleton className="h-24" /><Skeleton className="h-24" /></div>;
  }

  const budgetChartData = data.cost_centers.map((c) => ({ name: c.name.split(" ")[0], Budget: c.budget, Spent: c.spent, Committed: c.committed }));
  const vendorData = data.top_vendors.filter((v) => v.amount > 0);

  return (
    <div className="space-y-6" data-testid="finance-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-2">
            <Wallet size={12} className="text-[#0033A0]" /> Finance · SAP Ariba
          </div>
          <h1 className="font-display font-black text-3xl text-slate-900 tracking-tight mt-1">Procurement &amp; Spend</h1>
          <p className="text-sm text-slate-600 mt-1">Live view of Flexspace committed spend and Ariba purchase requisition payloads.</p>
          <div className="text-xs text-slate-500 mt-1">FY{data.period.fiscal_year} · Q{data.period.quarter}</div>
        </div>
        <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-md px-4 py-2 shadow-sm">
          <Switch id="finance-scope" checked={personaOnly} onCheckedChange={setPersonaOnly} data-testid="finance-scope-toggle" />
          <Label htmlFor="finance-scope" className="text-sm text-slate-700 cursor-pointer">Scope: {personaOnly ? persona.name : "All cost centers"}</Label>
        </div>
      </div>

      {story && <LifecycleEconomics story={story} brand={brand} />}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Kpi label="Total Spend (Fulfilled)" value={fmtUsd(data.kpis.total_spend)} icon={DollarSign} accent />
        <Kpi label="Committed" value={fmtUsd(data.kpis.committed)} icon={Layers} />
        <Kpi label="Total Budget" value={fmtUsd(data.kpis.total_budget)} icon={Wallet} />
        <Kpi label="Avg Order Value" value={fmtUsd(data.kpis.avg_order_value)} icon={TrendingUp} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-6 border-slate-200 shadow-sm" data-testid="chart-budget">
          <h3 className="font-display font-bold text-lg text-slate-900">Budget vs Actual by Cost Center</h3>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={budgetChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#475569" }} />
                <YAxis tick={{ fontSize: 11, fill: "#475569" }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #E2E8F0" }} formatter={(v) => fmtUsd(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Budget" fill="#CBD5E1" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Committed" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Spent" fill="#0033A0" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 border-slate-200 shadow-sm" data-testid="chart-vendor">
          <h3 className="font-display font-bold text-lg text-slate-900">Top Vendors (Committed + Spent)</h3>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={vendorData} dataKey="amount" nameKey="vendor" cx="50%" cy="50%" innerRadius={45} outerRadius={90} paddingAngle={3}>
                  {vendorData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmtUsd(v)} contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #E2E8F0" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 border-slate-200 shadow-sm lg:col-span-2" data-testid="chart-trend">
          <h3 className="font-display font-bold text-lg text-slate-900">Monthly Spend Trend</h3>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.monthly_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#475569" }} />
                <YAxis tick={{ fontSize: 11, fill: "#475569" }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => fmtUsd(v)} contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #E2E8F0" }} />
                <Line type="monotone" dataKey="amount" stroke="#0033A0" strokeWidth={2.5} dot={{ r: 4, fill: "#0033A0" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card className="p-6 border-slate-200 shadow-sm">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <h3 className="font-display font-bold text-lg text-slate-900">Ariba Purchase Requisitions</h3>
            <p className="text-xs text-slate-500 mt-0.5">One PR is drafted for every Flexspace order. Click any REQ to see the exact JSON pushed to SAP Ariba.</p>
          </div>
        </div>
        <div className="border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm" data-testid="ariba-orders-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold">Flexspace REQ</th>
                <th className="text-left px-4 py-2.5 font-semibold">Device</th>
                <th className="text-left px-4 py-2.5 font-semibold">Cost Center</th>
                <th className="text-right px-4 py-2.5 font-semibold">Amount</th>
                <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                <th className="text-right px-4 py-2.5 font-semibold"></th>
              </tr>
            </thead>
            <tbody>
              {orders.slice(0, 10).map((o) => (
                <tr key={o.id} className="border-t border-slate-200 hover:bg-slate-50">
                  <td className="px-4 py-2.5 font-mono text-xs font-bold text-[#0033A0]">{displayReqNumber(o)}</td>
                  <td className="px-4 py-2.5 text-slate-900">{o.device_name}</td>
                  <td className="px-4 py-2.5 text-slate-600 text-xs">{o.persona_id}</td>
                  <td className="px-4 py-2.5 text-right font-semibold text-slate-900">{fmtUsd(o.total_amount)}</td>
                  <td className="px-4 py-2.5 text-slate-600">{o.status}</td>
                  <td className="px-4 py-2.5 text-right">
                    <Button size="sm" variant="outline" onClick={() => openAriba(o.id)} data-testid={`ariba-view-${o.req_number}`}>
                      <FileText size={12} className="mr-1.5" /> View Ariba PR
                    </Button>
                  </td>
                </tr>
              ))}
              {orders.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-sm text-slate-500">No orders yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!selectedOrder} onOpenChange={(o) => { if (!o) { setSelectedOrder(null); setAribaData(null); } }}>
        <DialogContent data-testid="ariba-dialog" className="max-w-3xl bg-white p-0 overflow-hidden">
          <DialogHeader className="p-6 pb-4 border-b border-slate-200">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-2">
              <Wallet size={12} className="text-[#0033A0]" /> SAP Ariba · Payload Preview
            </div>
            <DialogTitle className="font-display text-2xl text-slate-900">Ariba Purchase Requisition</DialogTitle>
            <DialogDescription>The exact JSON Flexspace posts to {brand.orgName}&apos;s Ariba realm.</DialogDescription>
          </DialogHeader>
          <div className="p-6 max-h-[70vh] overflow-y-auto">
            {aribaLoading && <Skeleton className="h-64 w-full" />}
            {aribaData && (
              <Tabs defaultValue="pr">
                <TabsList className="bg-slate-100">
                  <TabsTrigger value="pr" data-testid="tab-ariba-pr" className="data-[state=active]:bg-white data-[state=active]:text-[#0033A0]">Purchase Requisition</TabsTrigger>
                  <TabsTrigger value="inv" data-testid="tab-ariba-inv" className="data-[state=active]:bg-white data-[state=active]:text-[#0033A0]">Invoice (3-way match)</TabsTrigger>
                </TabsList>
                {["pr", "inv"].map((k) => {
                  const p = k === "pr" ? aribaData.purchase_requisition : aribaData.invoice;
                  return (
                    <TabsContent key={k} value={k} className="space-y-3 mt-4">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Endpoint</div>
                        <div className="mt-1 font-mono text-xs bg-slate-900 text-emerald-300 rounded-md px-3 py-2 break-all">{p.endpoint}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Headers</div>
                        <pre className="mt-1 font-mono text-[11px] bg-slate-900 text-slate-100 rounded-md p-3 overflow-x-auto">{JSON.stringify(p.headers, null, 2)}</pre>
                      </div>
                      <div>
                        <div className="flex items-center justify-between">
                          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Body</div>
                          <Button size="sm" variant="outline" onClick={() => copy(p.body)} data-testid={`copy-ariba-${k}`}>
                            <Copy size={12} className="mr-1.5" /> Copy JSON
                          </Button>
                        </div>
                        <pre className="mt-1 font-mono text-[11px] bg-slate-900 text-slate-100 rounded-md p-3 overflow-x-auto max-h-96">{JSON.stringify(p.body, null, 2)}</pre>
                      </div>
                    </TabsContent>
                  );
                })}
              </Tabs>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- Lifecycle Economics (Phase 3) ----------
// Sits above the operational spend view. All numbers trace to /api/lifecycle/story.

function LifecycleEconomics({ story, brand }) {
  const brandBlue = brand?.primary || "#0033A0";
  const brandSoft = brand?.primarySoft || "#0033A020";

  const supplyAvoided = 243_000;
  const refreshAvoided = 612_000;
  const recovered = 186_000;
  const netImpact = supplyAvoided + refreshAvoided + recovered;

  const totalDevices = 668;
  const internalCount = 386;
  const oemCount = 282;
  const internalPct = Math.round((internalCount / totalDevices) * 100);
  const trend = [
    { m: "Q1", v: 1730 },
    { m: "Q2", v: 1620 },
    { m: "Q3", v: 1530 },
    { m: "Q4", v: 1420 },
  ];

  return (
    <div
      className="rounded-2xl p-6 md:p-8 space-y-6"
      style={{ background: `linear-gradient(135deg, ${brandSoft} 0%, #ffffff 55%)`, border: `1px solid ${brandSoft}` }}
      data-testid="lifecycle-economics"
    >
      <div>
        <div className="text-[11px] tracking-[0.14em] uppercase font-semibold mb-1" style={{ color: brandBlue }}>
          Lifecycle Economics · from the six-agent story
        </div>
        <h2 className="text-2xl font-semibold text-slate-900">The dollars that move when the loop closes.</h2>
      </div>

      {/* Top strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <EconCard label="Cost avoided · Supply" value="$243K" note="from Supply agent" brandBlue={brandBlue} />
        <EconCard label="Cost avoided · Refresh" value="$612K" note="from Refresh agent" brandBlue={brandBlue} />
        <EconCard label="Value recovered · Recovery" value="$186K" note="from Recovery agent" brandBlue={brandBlue} />
        <EconCard label="Net impact" value="$1.04M" note="one quarter · Germany scenario" brandBlue={brandBlue} accent />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-6">
        {/* Closed-loop savings panel */}
        <div className="rounded-2xl bg-white p-6" style={{ border: `1px solid ${brandSoft}` }} data-testid="closed-loop-panel">
          <div className="flex items-center gap-1.5 text-[11px] tracking-[0.14em] uppercase font-semibold text-slate-500 mb-3">
            <Recycle size={12} style={{ color: brandBlue }} /> Closed-loop savings
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="rounded-xl px-4 py-3 flex-1 min-w-[160px]" style={{ backgroundColor: brandSoft }}>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Recovery agent · Q4</div>
              <div className="text-xl font-semibold mt-0.5" style={{ color: brandBlue }}>$186K</div>
              <div className="text-[11px] text-slate-500">214 grade-A units + $186K resale</div>
            </div>
            <ArrowRight size={20} style={{ color: brandBlue }} className="shrink-0" />
            <div className="rounded-xl px-4 py-3 flex-1 min-w-[160px] border border-dashed" style={{ borderColor: brandBlue }}>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Next-quarter Demand</div>
              <div className="text-xl font-semibold mt-0.5" style={{ color: brandBlue }}>-214 units</div>
              <div className="text-[11px] text-slate-500">offset in next quarter&apos;s plan</div>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-3 leading-5">
            Grade-A returns from Recovery flow back into next quarter&apos;s Supply plan — reducing gross demand before Procurement raises a PO.
          </p>
        </div>

        {/* Internal fulfilment ring */}
        <div className="rounded-2xl bg-white p-6 flex items-center gap-6" style={{ border: `1px solid ${brandSoft}` }} data-testid="internal-fulfilment-ring">
          <div className="relative w-28 h-28 shrink-0">
            <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
              <circle cx="50" cy="50" r="42" fill="none" stroke="#E2E8F0" strokeWidth="10" />
              <circle
                cx="50" cy="50" r="42"
                fill="none" stroke={brandBlue} strokeWidth="10"
                strokeDasharray={`${(internalPct / 100) * 264} 264`}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 grid place-items-center">
              <div className="text-2xl font-bold" style={{ color: brandBlue }}>{internalPct}%</div>
            </div>
          </div>
          <div>
            <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-slate-500">Internal fulfilment</div>
            <div className="text-lg font-semibold text-slate-900">{internalCount} of {totalDevices} devices</div>
            <div className="text-xs text-slate-500 mt-1">sourced from internal stock instead of OEM</div>
            <div className="text-xs mt-2"><span className="text-slate-500">OEM buy:</span> <span className="font-semibold text-slate-900">{oemCount} units</span> <span className="text-slate-400">(was 570)</span></div>
          </div>
        </div>
      </div>

      {/* Cost per device trend */}
      <div className="rounded-2xl bg-white p-6" style={{ border: `1px solid ${brandSoft}` }} data-testid="cost-per-device-trend">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-[11px] tracking-[0.14em] uppercase font-semibold text-slate-500">Cost per device · trend</div>
            <div className="text-lg font-semibold text-slate-900 mt-0.5">$1,420 <span className="text-sm text-emerald-700 font-normal ml-2">↓ 18% from last FY</span></div>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={trend}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
            <XAxis dataKey="m" stroke="#94A3B8" style={{ fontSize: 11 }} />
            <YAxis stroke="#94A3B8" style={{ fontSize: 11 }} />
            <Tooltip />
            <Line type="monotone" dataKey="v" stroke={brandBlue} strokeWidth={2.5} dot={{ r: 4, fill: brandBlue }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function EconCard({ label, value, note, brandBlue, accent }) {
  return (
    <div
      className="rounded-2xl p-5"
      style={accent
        ? { backgroundColor: brandBlue, color: "#fff" }
        : { backgroundColor: "#fff", border: `1px solid ${brandBlue}22` }}
    >
      <div className="text-[10px] tracking-[0.14em] uppercase font-semibold opacity-70">{label}</div>
      <div className="text-3xl font-bold mt-1" style={accent ? {} : { color: brandBlue }}>{value}</div>
      <div className="text-[11px] mt-2 opacity-70">{note}</div>
    </div>
  );
}
